"use client";

import { Children, isValidElement, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SplitHover } from "@/components/ui/SplitHover";
import { useInViewOnce } from "@/lib/useInViewOnce";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1] as const;
const DURATION = 0.8;

/**
 * Applies the per-letter hover to the plain text inside a title, and leaves
 * everything else alone.
 *
 * Titles are passed as JSX, usually a fragment holding a string and an <Accent>,
 * so the split cannot just take a string. This walks one level: bare strings are
 * split, <Accent> keeps its colour and splits its own text (it renders SplitHover
 * itself), and any other element is returned untouched rather than guessed at.
 * A <br /> survives this way, which is what keeps a two-line title on two lines.
 */
const splitTitle = (node: ReactNode): ReactNode =>
  Children.map(node, (child) => {
    if (typeof child === "string") return <SplitHover>{child}</SplitHover>;
    if (isValidElement(child)) return child;
    // Numbers, null, booleans: nothing to split, nothing to hover.
    return child;
  });

/**
 * The section opener used across the page: optional mono eyebrow, big slab title,
 * accent bar, and an optional aside that sits to the right on desktop.
 *
 * Every opener enters the same way, so the page has one motion identity: the
 * eyebrow and title ride up from behind a clipped edge, then the accent bar draws
 * itself under them. Sequence is eyebrow 0s, title 0.05s, bar 0.25s.
 *
 * The reveal is driven by one IntersectionObserver on the outer wrapper rather
 * than by `whileInView` on each masked line. A line translated below its own
 * `overflow: hidden` edge has no intersection area to report, so per-line
 * `whileInView` would leave the headline clipped out of sight permanently.
 * See lib/useInViewOnce.ts.
 *
 * Under reduced motion the final state renders immediately with no duration, so
 * the server and client markup stay identical and nothing is patched on hydration.
 *
 *   <SectionHeader title={<>How we <Accent>work.</Accent></>} aside={<p>…</p>} />
 */
export interface SectionHeaderProps {
  title: ReactNode;
  /** Small mono label above the title */
  eyebrow?: ReactNode;
  /** Copy that sits to the right of the title on desktop, below it on mobile */
  aside?: ReactNode;
  /** Title scale. "xl" is the full-bleed 120px opener; "lg" tops out at 96px. */
  size?: "xl" | "lg";
  align?: "left" | "center";
  /** Id for aria-labelledby on the section */
  headingId?: string;
  className?: string;
}

export const SectionHeader = ({
  title,
  eyebrow,
  aside,
  size = "xl",
  align = "left",
  headingId,
  className,
}: SectionHeaderProps) => {
  const reduce = useReducedMotion();
  const [ref, seen] = useInViewOnce<HTMLDivElement>();
  // The mask clips while the line rides up, and must be released afterwards or it
  // slices the top off every letter that lifts on hover. Reduced motion never
  // animates, so there is no entrance to wait on and the clip is dropped at once.
  const [settled, setSettled] = useState(false);

  // Reduced motion renders the settled state on the first paint, so the type is
  // never clipped waiting on an observer that will not animate it anyway.
  const shown = reduce || seen;
  const unclipped = reduce || settled;
  /** A line riding up from behind its mask. */
  const line = (delay: number) => ({
    initial: { y: "110%" },
    animate: shown ? { y: "0%" } : { y: "110%" },
    transition: { duration: reduce ? 0 : DURATION, ease: EASE, delay: reduce ? 0 : delay },
  });

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-5 sm:gap-6",
        align === "left" ? "md:flex-row md:items-end md:justify-between md:gap-8" : "items-center text-center",
        className,
      )}
    >
      <div className={cn(align === "center" && "flex flex-col items-center")}>
        {eyebrow && (
          <span className="line-mask mb-4 sm:mb-5">
            <motion.span
              {...line(0)}
              className="block font-mono text-[10px] uppercase tracking-[0.2em] text-brand-ink/55 sm:text-xs"
            >
              {eyebrow}
            </motion.span>
          </span>
        )}
        <h2
          id={headingId}
          className={cn(
            "line-mask font-heading uppercase leading-[0.85] tracking-tighter text-brand-ink sm:leading-[0.8]",
            unclipped && "line-mask--done",
            size === "xl"
              ? "text-3xl sm:text-4xl md:text-6xl lg:text-[120px]"
              : "text-4xl sm:text-6xl md:text-7xl lg:text-[96px]",
          )}
        >
          {/* Gated on `shown`: this also fires when the hidden state finishes
              settling, which would drop the clip before the line has moved. */}
          <motion.span
            {...line(0.05)}
            onAnimationComplete={() => shown && setSettled(true)}
            className="block"
          >
            {splitTitle(title)}
          </motion.span>
        </h2>
        <motion.div
          aria-hidden
          initial={{ scaleX: 0 }}
          animate={shown ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: reduce ? 0 : 0.7, ease: EASE, delay: reduce ? 0 : 0.25 }}
          className={cn(
            "mt-4 h-1.5 w-16 bg-brand-mint sm:mt-5 sm:h-2 sm:w-24 md:mt-6 md:w-32",
            align === "center" ? "mx-auto origin-center" : "origin-left",
          )}
        />
      </div>
      {aside && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={shown ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          transition={{ duration: reduce ? 0 : DURATION, ease: EASE, delay: reduce ? 0 : 0.15 }}
          className={cn("max-w-md", align === "left" ? "md:max-w-sm md:text-right" : "mx-auto")}
        >
          {aside}
        </motion.div>
      )}
    </div>
  );
};

/**
 * The accent-coloured word inside a section title or aside.
 *
 * A plain string splits into per-letter hover targets, matching the rest of the
 * headline; anything else renders as before. The hover colour is suppressed here
 * because these letters are already pine, and lifting them is the whole tell.
 */
export const Accent = ({ children }: { children: ReactNode }) =>
  typeof children === "string" ? (
    <span className="text-brand-accent">
      <SplitHover hoverClassName="hover:text-brand-ink">{children}</SplitHover>
    </span>
  ) : (
    <span className="text-brand-accent">{children}</span>
  );
